import { BaseMode } from '../BaseMode.js';
import { API_URL, fetchWithTimeout } from '../../core/api.js';
import { getThinkingGroup, pickHeader } from '../../constants/thinkingGroups.js';
import { escapeHtml } from '../../core/utils.js';

// Only these entry types get a distinct label — every other entry type
// (`normal`, `autocomplete`, `topic_transition`,
// `mode_handoff`, `orientation_advice`, legacy/retired types) falls through to
// the generic "Chat Session" label below.
const MODE_LABELS = {
  session_continuation: 'Discuss Session',
  explore_question: 'Discuss Thread',
  memo_entry: 'Discuss Note',
  memo_compose: 'Compose Note',
  guide_insight: 'Discuss Guide',
  shared_guide_insight: 'Discuss Together Guide',
  discuss_pattern: 'Discuss Pattern',
  discuss_framework: 'Discuss Assessment'
};

// Header phases that exist only while a tool is executing mid-turn. They are
// backend `advisingPhase` emissions, so nothing clears them when the turn ends
// — the header strategy resets them on any terminal event. `scoping` and
// `entry` are deliberately absent: those span turns and own their own exits.
const TOOL_PHASES = new Set([
  'retrieving',
  'choosing_practice',
  'designing_practice',
  'composing_memo',
  'composing_takeaway'
]);

/**
 * Advice Mode - General therapeutic conversation
 * All user messages go to LLM for empathetic responses
 */
export class AdviceMode extends BaseMode {
  constructor() {
    super({
      name: 'ADVICE',
      contextBuilder: null // TODO: Implement AdviceContextBuilder
    });
    this.entryType = null;
    this._resetThinkingLinesState();
  }

  /**
   * Reset the thinking-lines client state (priorLines + stashed title +
   * turnToken — plan §2.4). Called from BOTH onEnter and onRestore: instance
   * fields don't get the header phase machine's free closure-recreation reset
   * on a plain mode switch, and both lifecycle paths exist.
   * @private
   */
  _resetThinkingLinesState() {
    /** @type {string[]} Theater lines generated so far this session (≤15, most recent last). */
    this._priorThinkingLines = [];
    /** @type {string|null} Stashed title for the `advising`-phase header render. */
    this._stashedTitle = null;
    this._stashedTitleChanged = false;
    // Resume-into-private (plan §2.3): `sessionMeta.descriptor` on a private
    // session is the LAST PUBLIC descriptor (privacy is enable-only; nothing
    // regenerates it once private), not "Private Conversation" — so a stashed
    // title alone can't tell `mode:enter` to render the private state. This
    // flag carries that alongside the stash.
    this._stashedPrivacyMode = false;
    /** @type {number} Monotonic per-turn token — see bumpTurnToken(). */
    this._turnToken = 0;
  }

  /**
   * Cache the entry type so getModeLabel() can read it without reaching into
   * ModeOrchestrator internals. Mirrors TreatmentMode.subMode / InsightMode.variant.
   */
  async onEnter(previousMode, transitionData = {}) {
    await super.onEnter(previousMode, transitionData);
    this.entryType = transitionData.entryType ?? null;
    this._resetThinkingLinesState();
  }

  /**
   * Restore path (page reload / dashboard return into an existing session):
   * ModeOrchestrator.restoreMode() has no transitionData, so entryType is
   * threaded in via fetched session metadata instead. Also stashes the
   * session's persisted title (`sessionMeta.descriptor`) so the `mode:enter`
   * header render (which fires right after this, per the restoreMode
   * ordering fix) has it available immediately instead of falling through to
   * the legacy fetch fallback — and the session's live `privacyMode` flag, so
   * a resume into an already-private session renders "Private Conversation"
   * rather than the stale pre-privacy descriptor (see `_resetThinkingLinesState`).
   * @param {Object|null} sessionMeta - Session metadata, or null if unavailable
   */
  async onRestore(sessionMeta) {
    this.entryType = sessionMeta?.entryType ?? null;
    this._resetThinkingLinesState();
    this._stashedTitle = sessionMeta?.descriptor ?? null;
    this._stashedPrivacyMode = sessionMeta?.privacyMode === true;
  }

  /** Born-private seed (dashboard pre-arm): called by switchMode after onEnter has reset
   *  state and before the header strategy is activated, with the SERVER-echoed flag from
   *  the creation response — never the client's request or PrivacyModeManager's live flag. */
  seedBornPrivate() { this._stashedPrivacyMode = true; }

  /**
   * N9 ownership guard: bump the per-turn token. Called once per user turn
   * dispatched through this mode (ModeOrchestrator.handleUserMessage) — the
   * client-side analog of HeaderContentService's `_activationToken`. A
   * {sessionId, turnToken} pair captured before this call is stale the moment
   * it returns.
   * @returns {number} The new current token
   */
  bumpTurnToken() {
    this._turnToken += 1;
    return this._turnToken;
  }

  /**
   * @returns {number} Current turn token, for the N9 staleness check on a
   *   thinking-lines response.
   */
  getTurnToken() {
    return this._turnToken;
  }

  /**
   * @returns {string[]} Thinking-lines bank generated so far this session
   *   (≤15, most recent last) — sent as `priorLines` on the next request.
   */
  getPriorThinkingLines() {
    return this._priorThinkingLines;
  }

  /**
   * Append newly-generated theater lines to the session's avoid-list bank.
   * Single cap: 15 stored = 15 sent (plan §2.4).
   * @param {string[]} lines
   */
  appendPriorThinkingLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return;
    this._priorThinkingLines = [...this._priorThinkingLines, ...lines].slice(-15);
  }

  /**
   * Stash a thinking-lines-generated (or session-restored) title for the
   * `advising`-phase header render. A null/undefined title is a no-op — the
   * caller already has fail-open semantics upstream (server + fetch layer).
   * @param {string|null} title
   * @param {boolean} [titleChanged=false]
   */
  stashTitle(title, titleChanged = false) {
    if (title == null) return;
    this._stashedTitle = title;
    this._stashedTitleChanged = titleChanged === true;
  }

  getModeLabel() {
    return MODE_LABELS[this.entryType] || 'Chat Session';
  }

  /**
   * Live-update the entry type after an in-place session flip — currently
   * only the R2 typed note-intent route (`POST .../scope/start-note-compose`)
   * — so `getModeLabel()` reflects the new entry without a reload. Distinct
   * from `onEnter`/`onRestore`, which only run at mode-transition/session-
   * restore boundaries; this is the live third writer for the same field.
   * The caller (ActionRouter) is expected to re-render the header label
   * immediately after calling this, since `getModeLabel()` is otherwise read
   * once per mode change (see BaseMode's header-system docs).
   * @param {string} entryType
   */
  setEntryType(entryType) {
    this.entryType = entryType;
  }

  getRoutingStrategy(message, context) {
    // All messages in advice mode go to LLM
    return 'llm';
  }

  filterHistory(allMessages) {
    // In advice mode, filter out discovery questionnaire messages (too noisy)
    return allMessages.filter(msg =>
      msg.mode !== 'DISCOVERY' || msg.role === 'user'
    );
  }

  async buildContext(userId, message, messageHistory) {
    const filteredHistory = this.filterHistory(messageHistory);

    return {
      mode: this.name,
      recentMessages: filteredHistory.slice(-15), // More history for advice mode
      // TODO: Add user insights, patterns, etc.
    };
  }

  /**
   * Header content strategy for Advice Mode
   * Phase-aware state machine: entry → scoping → (retrieving | composing_memo |
   * choosing_practice → designing_practice) → advising
   *
   * @returns {Object} Strategy with phase-tracking header logic
   */
  getHeaderContentStrategy() {
    // 'entry' | 'scoping' | 'retrieving' | 'composing_memo' | 'choosing_practice'
    // | 'designing_practice' | 'composing_takeaway' | 'advising'
    let phase = 'entry';
    const ELLIPSIS = '<span class="thinking-indicator__ellipsis"></span>';
    // Header copy lives in the shared registry; phase names are group keys.
    const headerFor = (key) => pickHeader(getThinkingGroup(key));

    // N8: titles are untrusted text (server-validated, but escaped again here
    // as the client's own hardening) — every title reaching this sink must be
    // escaped before combining with the trusted ELLIPSIS markup, since
    // HeaderManager writes the middle block via innerHTML.
    const renderTitle = (title, showEllipsis = true) =>
      `${escapeHtml(title)}${showEllipsis ? ELLIPSIS : ''}`;

    // ONE legacy GET /chat/descriptor call, kept only as the genuine
    // metadata-fetch-failure fallback (thinking-lines-header-plan §2.3): the
    // `advising` phase renders the stashed title rather than calling this.
    // `mode:enter` and the privacy-exit edge fall back to it only when there
    // is nothing stashed (a failed/empty sessionMeta fetch, or a session that
    // went private before any typed ADVICE turn).
    const fetchDescriptor = async () => {
      const sessionId = window.sessionManager?.getCurrentSessionId();
      if (!sessionId) return null;
      try {
        const response = await fetchWithTimeout(`${API_URL}/chat/descriptor/${sessionId}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data.success || !data.data?.descriptor) return null;
        // Surface privacyMode alongside the descriptor (rather than returning just
        // the descriptor string) so callers can suppress the animated ellipsis over
        // the static "Private Conversation" text.
        return { descriptor: data.data.descriptor, privacyMode: data.data.privacyMode === true };
      } catch {
        return null;
      }
    };

    return {
      /**
       * Generate header text based on current conversation phase
       *
       * @param {Object} context - Context with event name (+ `privacyMode` for
       *   `session:privacyModeChanged` / `active` for `privacy:changed`, each
       *   echoed verbatim from its eventBus payload)
       * @returns {Promise<{text: string}>} Phase-appropriate header text with animated ellipsis
       */
      generateContent: async (context) => {
        // Privacy is a persistent header STATE, not a one-shot event render.
        // It used to be the latter, and every later phase re-render
        // (`message:received` in `advising`, a tool `state:update`, the
        // `stream:complete` reset) overwrote "Private Conversation" with the
        // phase fallback — "Starting conversation" when nothing was stashed,
        // or, worse, the last PUBLIC descriptor when something was. So: fold
        // the state edges into the flag first, then let the flag win over the
        // whole phase machine below.

        // Exit is the one edge that must beat the state check: the cancel
        // route emits this event BEFORE PrivacyModeManager flips
        // `isPrivacyMode`, so the live getter still reads true here.
        if (context.event === 'session:privacyModeChanged' && context.privacyMode !== true) {
          phase = 'advising';
          this._stashedPrivacyMode = false;
          // A session may have gone private before any typed ADVICE turn ever
          // stashed a title, so the legacy fetch is the last resort here too.
          if (this._stashedTitle) {
            return { text: renderTitle(this._stashedTitle) };
          }
          const result = await fetchDescriptor();
          if (result?.descriptor) {
            return { text: renderTitle(result.descriptor, !result.privacyMode) };
          }
          return { text: `${headerFor('entry')}${ELLIPSIS}` };
        }

        // Entry edges, both mid-session: `session:privacyModeChanged` is the
        // kebab entry; `privacy:changed {active:true}` is PrivacyModeManager's
        // decoupled echo of the same edge. Born-private (dashboard pre-arm) is
        // NOT an event edge here — its `privacy:changed` rides `session:started`
        // before this strategy is subscribed, so `switchMode` seeds the flag
        // from the creation response instead (`seedBornPrivate`).
        if ((context.event === 'session:privacyModeChanged' && context.privacyMode === true)
          || (context.event === 'privacy:changed' && context.active === true)) {
          this._stashedPrivacyMode = true;
        }

        // The state check itself. `_stashedPrivacyMode` also carries the
        // resume-into-private case from onRestore (§2.3) — a private session's
        // stashed descriptor is the last PUBLIC one, so this must precede the
        // title stash below.
        //
        // Deliberately NOT backstopped by PrivacyModeManager's live getter:
        // that flag is only cleared on navigation back to the DASHBOARD, so it
        // can read true on a page the user reached from a private chat, and a
        // public session inheriting it would claim a privacy it doesn't have.
        // Under-claiming is the safe direction here; over-claiming is not.
        // Every entry edge is covered by an event above, so the flag is
        // sufficient — as long as the off edges clear it (see shouldUpdate).
        if (this._stashedPrivacyMode) {
          // Static: no fetch, no ellipsis — and no phase can overwrite it,
          // because every subsequent trigger lands right back here.
          phase = 'advising';
          return { text: 'Private Conversation' };
        }

        if (context.event === 'mode:enter') {
          // onRestore (when this is a resume) already stashed
          // sessionMeta?.descriptor — see the restoreMode ordering fix. A
          // fresh onEnter (brand-new session) has nothing stashed yet.
          if (this._stashedTitle) {
            phase = 'advising';
            return { text: renderTitle(this._stashedTitle) };
          }
          const result = await fetchDescriptor();
          if (result?.descriptor) {
            phase = 'advising';
            return { text: renderTitle(result.descriptor, !result.privacyMode) };
          }
          return { text: `${headerFor('entry')}${ELLIPSIS}` };
        }

        if (phase === 'scoping') {
          return { text: `${headerFor('scoping')}${ELLIPSIS}` };
        }
        if (TOOL_PHASES.has(phase)) {
          return { text: `${headerFor(phase)}${ELLIPSIS}` };
        }
        if (phase === 'advising') {
          // The thinking-lines POST (fired at send time) is what keeps this
          // stash current; no fetch here any more.
          if (this._stashedTitle) {
            return { text: renderTitle(this._stashedTitle) };
          }
          return { text: `${headerFor('entry')}${ELLIPSIS}` };
        }

        return { text: `${headerFor('entry')}${ELLIPSIS}` };
      },

      /**
       * Events that trigger header updates
       * @returns {string[]} Event names
       */
      getUpdateTriggers: () => ['message:received', 'state:update', 'stream:complete', 'stream:failed', 'session:privacyModeChanged', 'privacy:changed'],

      /**
       * Advance phase state machine and decide whether to re-render header
       *
       * @param {string} event - Event name
       * @param {Object} eventData - Event payload
       * @returns {boolean} True if header should be re-rendered
       */
      shouldUpdate: (event, eventData) => {
        if (event === 'message:received') {
          if (phase === 'entry') {
            phase = 'scoping';
            return true; // Show "Gathering information"
          }
          if (phase === 'advising') {
            return true; // Re-render off the current title stash
          }
          return false; // scoping/retrieving: header already correct, skip
        }

        if (event === 'state:update') {
          const advisingPhase = eventData?.updates?.advisingPhase;
          if (advisingPhase === phase) return false;
          // TOOL_PHASES doubles as the allow-list: membership drives both this
          // transition and the terminal-event reset below, and each member's
          // header copy comes from the registry (headerFor above).
          if (advisingPhase && TOOL_PHASES.has(advisingPhase)) {
            phase = advisingPhase;
            return true;
          }
          return false;
        }

        // Terminal events end the turn, so any phase the turn's tools set is
        // stale. Resetting only on `contextRetrieved` left a tool phase pinned
        // whenever no retrieval happened — the header still read "Designing a
        // practice…" after a *successful* composePractice turn (and after any
        // failed or stopped one), until the user's next message.
        //
        // Only TOOL_PHASES are cleared: `scoping` legitimately spans turns
        // until refreshContext fires, so a blanket reset to `advising` would
        // show a conversation descriptor mid-scoping.
        if (event === 'stream:complete' || event === 'stream:failed') {
          const contextRetrieved = eventData?.data?.metadata?.contextRetrieved === true;
          if (TOOL_PHASES.has(phase) || (contextRetrieved && phase !== 'advising')) {
            phase = 'advising';
            return true; // Fetch and display descriptor immediately
          }
          return false;
        }

        // Privacy entered mid-conversation (⚠R1 W4): force the
        // (synchronous, fetch-free) render immediately regardless of current
        // phase, so "Private Conversation" renders even during
        // scoping/retrieving.
        if (event === 'session:privacyModeChanged') {
          return true;
        }

        // Mid-session privacy entry, as echoed by PrivacyModeManager. A
        // born-private session's `privacy:changed` fires before this strategy
        // is subscribed and is seeded by `switchMode` (`seedBornPrivate`), so
        // the on edge here only ever means an in-session entry.
        if (event === 'privacy:changed') {
          if (eventData?.active === true) return true;

          // The off edge clears the flag WITHOUT re-rendering. Clearing is
          // load-bearing: `switchMode` early-returns on a same-mode call, so a
          // dashboard → new-ADVICE-session round trip can skip `onEnter` (and
          // its `_resetThinkingLinesState`) entirely, and a `_stashedPrivacyMode`
          // left set would pin "Private Conversation" onto the next PUBLIC
          // session. Not re-rendering is equally deliberate: this edge fires on
          // dashboard navigation, where the header is in its dashboard state and
          // must not be written to. The in-place exit (same-click cancel) emits
          // `session:privacyModeChanged` above and renders from there, so no
          // visible transition is lost.
          this._stashedPrivacyMode = false;
          return false;
        }

        return false;
      }
    };
  }

}
